import React, { useCallback } from "react";
import { commitMutation, graphql, useFragment } from "react-relay";

import { faLock, faUnlock } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";

import { showFeatureModal } from "~/billing";
import {
	MenuItemToBeDisabledForExternalWorkspaces,
	MenuItemTooltip,
	useGetPropsForExternalWorkspaces,
} from "~/meetings/MeetingHeader/MoreActionsMenu/MenuItemTooltip";
import { environment } from "~/models";
import toast from "~/ui/toast";

import type { LockNote_note$key } from "~/__generated__/LockNote_note.graphql";
import type { LockNote_viewer$key } from "~/__generated__/LockNote_viewer.graphql";
import type { LockNoteLockingMutation } from "~/__generated__/LockNoteLockingMutation.graphql";
import type { LockNoteUnlockingMutation } from "~/__generated__/LockNoteUnlockingMutation.graphql";

const viewerFragment = graphql`
	fragment LockNote_viewer on Viewer {
		account {
			billingAccount {
				hasAccessToNoteLocking: hasAccessToFeature(feature: note_locking)
			}
		}
	}
`;

const noteFragment = graphql`
	fragment LockNote_note on Note {
		id
		isLocked
		canUnlockNote
		...MenuItemTooltip_useGetPropsForExternalWorkspaces_note
	}
`;

function lockNote(noteId: string) {
	commitMutation<LockNoteLockingMutation>(environment, {
		mutation: graphql`
			mutation LockNoteLockingMutation($input: LockNoteInput!) {
				lockNote(input: $input) {
					note {
						id
						isLocked
						canUnlockNote
					}
				}
			}
		`,
		variables: {
			input: {
				noteId: noteId,
			},
		},
		onCompleted: data => {
			const { lockNote } = data;
			if (lockNote?.note) {
				toast.success("Note successfully locked");
			}
		},
	});
}

function unlockNote(noteId: string) {
	commitMutation<LockNoteUnlockingMutation>(environment, {
		mutation: graphql`
			mutation LockNoteUnlockingMutation($input: UnlockNoteInput!) {
				unlockNote(input: $input) {
					note {
						id
						isLocked
						canUnlockNote
					}
				}
			}
		`,
		variables: {
			input: {
				noteId: noteId,
			},
		},
		onCompleted: data => {
			const { unlockNote } = data;
			if (unlockNote?.note) {
				toast.success("Note successfully unlocked");
			}
		},
	});
}

export default function LockNoteMenuItem({
	viewer: viewerKey,
	note: noteKey,
	inAgendaBuilderMode,
}: {
	viewer: LockNote_viewer$key;
	note: LockNote_note$key;
	inAgendaBuilderMode?: boolean;
}) {
	const viewer = useFragment(viewerFragment, viewerKey);
	const hasAccessToNoteLocking = viewer.account.billingAccount.hasAccessToNoteLocking;

	const note = useFragment(noteFragment, noteKey);

	const tooltipPropsForExternalWorkspaces = useGetPropsForExternalWorkspaces(note);

	const onLockNote = useCallback(() => {
		if (!hasAccessToNoteLocking) {
			showFeatureModal("note_locking");
			return;
		}
		lockNote(note.id);
	}, [note.id, hasAccessToNoteLocking]);

	const onUnlockNote = useCallback(() => {
		if (!hasAccessToNoteLocking) {
			showFeatureModal("note_locking");
			return;
		}
		unlockNote(note.id);
	}, [note.id, hasAccessToNoteLocking]);

	if (note.isLocked) {
		return (
			<MenuItemToBeDisabledForExternalWorkspaces
				icon={!hasAccessToNoteLocking ? faArrowAltCircleUp : faUnlock}
				iconProps={{
					color: !hasAccessToNoteLocking ? "var(--billingColor)" : undefined,
				}}
				onSelect={onUnlockNote}
				disabled={!note.canUnlockNote}
				{...tooltipPropsForExternalWorkspaces}
			>
				<MenuItemTooltip disabled={!!note.canUnlockNote} content="You do not have permission to unlock this note.">
					<span>Unlock note</span>
				</MenuItemTooltip>
			</MenuItemToBeDisabledForExternalWorkspaces>
		);
	} else {
		return (
			<MenuItemToBeDisabledForExternalWorkspaces
				icon={!hasAccessToNoteLocking ? faArrowAltCircleUp : faLock}
				iconProps={{
					color: !hasAccessToNoteLocking ? "var(--billingColor)" : undefined,
				}}
				onSelect={onLockNote}
				disabled={inAgendaBuilderMode}
				{...tooltipPropsForExternalWorkspaces}
			>
				<MenuItemTooltip disabled={!inAgendaBuilderMode} content="Feature disabled in AI-generated mode.">
					<span>Lock note</span>
				</MenuItemTooltip>
			</MenuItemToBeDisabledForExternalWorkspaces>
		);
	}
}
